import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Badge from '../../components/ui/Badge';
import TagBadge from '../../components/common/TagBadge';
import {
  Trigger,
  TriggerAssetScope,
  TriggerAssignmentStrategy,
  Workflow,
  TeamMemberInfo,
  EventDefinition,
} from '../../types';
import { CreateTriggerRequest } from '../../services/triggerService';
import {
  ASSET_SCOPE_OPTIONS,
  ASSIGNMENT_STRATEGY_OPTIONS,
  getAssetScopeLabel,
  getEventCategoryLabel,
  getAssignmentStrategyLabel,
} from './triggerOptions';

// =============================================================================
// Shared field helpers (kept inline so the file is self-contained)
// =============================================================================

const fieldInputClass =
  'w-full h-9 px-3 bg-surface border border-neutral-200 rounded-lg text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none transition-colors duration-fast';

interface FieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-medium text-neutral-500 mb-1.5">
      {label}
      {required && <span className="text-danger-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

// =============================================================================
// Types & constants
// =============================================================================

interface TriggerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTriggerRequest, id?: string) => Promise<void>;
  editing: Trigger | null;
  eventDefinitions: EventDefinition[];
  workflows: Workflow[];
  teamMembers: TeamMemberInfo[];
}

interface FormState {
  name: string;
  description: string;
  isActive: boolean;
  eventDefinitionId: string;
  workflowId: string;
  assetScope: TriggerAssetScope;
  assignmentStrategy: TriggerAssignmentStrategy;
  assigneeUserId: string;
}

const BLANK_FORM: FormState = {
  name: '',
  description: '',
  isActive: true,
  eventDefinitionId: '',
  workflowId: '',
  assetScope: 'coverage',
  assignmentStrategy: 'round_robin',
  assigneeUserId: '',
};

type StepKey = 'event' | 'scope' | 'outcome' | 'review';

const STEPS: { key: StepKey; num: number; label: string }[] = [
  { key: 'event', num: 1, label: 'Event' },
  { key: 'scope', num: 2, label: 'Scope' },
  { key: 'outcome', num: 3, label: 'Outcome' },
  { key: 'review', num: 4, label: 'Review' },
];

// =============================================================================
// Modal
// =============================================================================

const TriggerFormModal: React.FC<TriggerFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editing,
  eventDefinitions,
  workflows,
  teamMembers,
}) => {
  const [form, setForm] = useState<FormState>(BLANK_FORM);
  const [step, setStep] = useState<StepKey>('event');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate form + reset to first step whenever the modal opens
  useEffect(() => {
    if (!isOpen) return;
    if (editing) {
      setForm({
        name: editing.name,
        description: editing.description ?? '',
        isActive: editing.isActive,
        eventDefinitionId: editing.eventDefinitionId,
        workflowId: editing.workflowId,
        assetScope: editing.assetScope,
        assignmentStrategy: editing.assignmentStrategy ?? 'round_robin',
        assigneeUserId: editing.assigneeUserId ?? '',
      });
    } else {
      setForm(BLANK_FORM);
    }
    setStep('event');
    setError(null);
  }, [isOpen, editing]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  // ─── Step gating ─────────────────────────────────────────────────────────
  const canProceed = (): boolean => {
    switch (step) {
      case 'event':
        return form.name.trim().length > 0 && !!form.eventDefinitionId;
      case 'scope':
        return true; // Both options are always valid; no sub-config
      case 'outcome': {
        if (!form.workflowId) return false;
        // Coverage-scoped triggers don't need a strategy
        if (form.assetScope === 'coverage') return true;
        if (!form.assignmentStrategy) return false;
        if (form.assignmentStrategy === 'specific_user' && !form.assigneeUserId) return false;
        return true;
      }
      case 'review':
        return true;
    }
  };

  const goNext = () => {
    const idx = STEPS.findIndex(s => s.key === step);
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1].key);
  };
  const goBack = () => {
    const idx = STEPS.findIndex(s => s.key === step);
    if (idx > 0) setStep(STEPS[idx - 1].key);
  };

  const isReviewStep = step === 'review';
  const isFirstStep = step === 'event';
  const currentStepNum = STEPS.find(s => s.key === step)?.num ?? 1;

  // ─── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setError(null);

    const isCoverage = form.assetScope === 'coverage';

    const payload: CreateTriggerRequest = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      isActive: form.isActive,
      eventDefinitionId: form.eventDefinitionId,
      workflowId: form.workflowId,
      assetScope: form.assetScope,
      // Only send strategy for event-scoped triggers
      assignmentStrategy: isCoverage ? undefined : form.assignmentStrategy,
      assigneeUserId:
        !isCoverage && form.assignmentStrategy === 'specific_user'
          ? form.assigneeUserId
          : undefined,
    };

    try {
      setSubmitting(true);
      await onSubmit(payload, editing?.id);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save trigger.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Lookup helpers for the review step
  const selectedEventDef = eventDefinitions.find(ed => ed.id === form.eventDefinitionId);
  const selectedWorkflow = workflows.find(w => w.id === form.workflowId);
  const selectedAssignee = teamMembers.find(m => m.id === form.assigneeUserId);

  // =============================================================================
  // Render
  // =============================================================================

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 animate-fade-in" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-surface rounded-xl shadow-2xl border border-neutral-200 max-w-3xl w-full mx-auto z-10 animate-scale-in flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-neutral-900">
              {editing ? 'Edit Trigger' : 'New Trigger'}
            </h2>
            <button
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600 transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress steps */}
          <div className="flex items-center mt-4 space-x-2">
            {STEPS.map((s, i) => {
              const isCompleted = s.num < currentStepNum;
              const isCurrent = s.key === step;
              return (
                <React.Fragment key={s.key}>
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                      isCompleted || isCurrent
                        ? 'bg-primary-600 text-white'
                        : 'bg-neutral-200 text-neutral-600'
                    }`}
                  >
                    {s.num}
                  </div>
                  <span
                    className={`text-sm ${
                      isCompleted || isCurrent ? 'text-primary-600 font-medium' : 'text-neutral-500'
                    }`}
                  >
                    {s.label}
                  </span>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 ${isCompleted ? 'bg-primary-600' : 'bg-neutral-200'}`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 overflow-y-auto">
          {error && (
            <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
              {error}
            </div>
          )}

          {/* ── Step 1 · Event ───────────────────────────────────── */}
          {step === 'event' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-base font-semibold text-neutral-900">Identity</h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  Name the trigger and pick the event it listens to.
                </p>
              </div>

              <Field label="Name" required>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => update('name', e.target.value)}
                  placeholder="e.g. Earnings Pre-Read"
                  className={fieldInputClass}
                />
              </Field>
              <Field label="Description">
                <input
                  type="text"
                  value={form.description}
                  onChange={e => update('description', e.target.value)}
                  placeholder="What this trigger is for"
                  className={fieldInputClass}
                />
              </Field>
              <label className="flex items-center gap-2 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={e => update('isActive', e.target.checked)}
                  className="h-4 w-4 rounded border-neutral-300"
                />
                Active
              </label>

              <Select
                label="Event definition"
                required
                value={form.eventDefinitionId}
                onChange={e => update('eventDefinitionId', e.target.value)}
                options={eventDefinitions.map(ed => ({
                  value: ed.id,
                  label: `${ed.name} · ${getEventCategoryLabel(ed.category)}`,
                }))}
                placeholder={
                  eventDefinitions.length === 0
                    ? 'No events yet — create one on the Events tab'
                    : 'Pick an event'
                }
              />
              {selectedEventDef && (
                <div className="p-3 bg-neutral-50 border border-neutral-200 rounded-lg">
                  <div className="flex items-center flex-wrap gap-2">
                    <Badge variant="primary" size="sm">
                      {getEventCategoryLabel(selectedEventDef.category)}
                    </Badge>
                    {selectedEventDef.tags.map(tag => (
                      <TagBadge key={tag.id} tag={tag} />
                    ))}
                  </div>
                  {selectedEventDef.description && (
                    <p className="text-xs text-neutral-500 mt-1.5">
                      {selectedEventDef.description}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Step 2 · Scope ───────────────────────────────────── */}
          {step === 'scope' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-base font-semibold text-neutral-900">Asset scope</h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  How is the asset for this trigger determined?
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {ASSET_SCOPE_OPTIONS.map(opt => {
                  const active = form.assetScope === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => update('assetScope', opt.value)}
                      className={`text-left px-3 py-3 rounded-lg border transition-colors ${
                        active
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-neutral-300 hover:border-neutral-400 bg-surface text-neutral-700'
                      }`}
                    >
                      <div className="text-sm font-medium">{opt.label}</div>
                      <div className="text-[11px] text-neutral-500 mt-0.5 leading-tight">
                        {opt.hint}
                      </div>
                    </button>
                  );
                })}
              </div>

              {form.assetScope === 'coverage' && (
                <div className="p-3 bg-neutral-50 border border-neutral-200 rounded-lg text-xs text-neutral-500">
                  The trigger will fire once per coverage relationship. The assignee
                  is the user who covers the asset — no further config needed.
                </div>
              )}
              {form.assetScope === 'event' && (
                <div className="p-3 bg-neutral-50 border border-neutral-200 rounded-lg text-xs text-neutral-500">
                  The asset comes from the event payload. You&apos;ll pick an
                  assignment strategy on the next step.
                </div>
              )}
            </div>
          )}

          {/* ── Step 3 · Outcome ─────────────────────────────────── */}
          {step === 'outcome' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-base font-semibold text-neutral-900">Outcome</h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  Pick which workflow fires
                  {form.assetScope === 'event' ? ' and who gets assigned.' : '.'}
                </p>
              </div>

              <Select
                label="Workflow to fire"
                required
                value={form.workflowId}
                onChange={e => update('workflowId', e.target.value)}
                options={workflows.map(w => ({ value: w.id, label: w.name }))}
                placeholder="Pick a workflow"
              />

              {form.assetScope === 'coverage' ? (
                <div className="p-3 bg-neutral-50 border border-neutral-200 rounded-lg text-xs text-neutral-500">
                  Assignment is implicit: whoever covers the asset gets the workflow.
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-2">
                      Assignment strategy
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {ASSIGNMENT_STRATEGY_OPTIONS.map(opt => {
                        const active = form.assignmentStrategy === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => update('assignmentStrategy', opt.value)}
                            className={`text-left px-3 py-2.5 rounded-lg border transition-colors ${
                              active
                                ? 'border-primary-500 bg-primary-50 text-primary-700'
                                : 'border-neutral-300 hover:border-neutral-400 bg-surface text-neutral-700'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{opt.label}</span>
                              {opt.comingSoon && (
                                <span className="text-[9px] uppercase tracking-wider text-neutral-400 font-semibold">
                                  Soon
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-neutral-500 mt-0.5 leading-tight">
                              {opt.hint}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {form.assignmentStrategy === 'specific_user' && (
                    <Select
                      label="Assign to"
                      value={form.assigneeUserId}
                      onChange={e => update('assigneeUserId', e.target.value)}
                      options={teamMembers.map(m => ({ value: m.id, label: m.fullName }))}
                      placeholder="Pick a team member"
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Step 4 · Review ──────────────────────────────────── */}
          {step === 'review' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-base font-semibold text-neutral-900">Review</h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  Double-check the trigger configuration before saving.
                </p>
              </div>

              <div className="border border-neutral-200 rounded-xl divide-y divide-neutral-200">
                <ReviewRow
                  label="Name"
                  value={form.name || <span className="text-neutral-400">—</span>}
                />
                {form.description && <ReviewRow label="Description" value={form.description} />}
                <ReviewRow
                  label="Status"
                  value={
                    <Badge variant={form.isActive ? 'success' : 'secondary'} size="sm">
                      {form.isActive ? 'Active' : 'Paused'}
                    </Badge>
                  }
                />
                <ReviewRow
                  label="Event"
                  value={
                    selectedEventDef ? (
                      <div>
                        <div>{selectedEventDef.name}</div>
                        <div className="text-xs text-neutral-500 mt-0.5">
                          {getEventCategoryLabel(selectedEventDef.category)}
                        </div>
                        {selectedEventDef.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {selectedEventDef.tags.map(tag => (
                              <TagBadge key={tag.id} tag={tag} />
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-danger-600">Not selected</span>
                    )
                  }
                />
                <ReviewRow label="Asset scope" value={getAssetScopeLabel(form.assetScope)} />
                <ReviewRow
                  label="Workflow"
                  value={
                    selectedWorkflow?.name || (
                      <span className="text-danger-600">Not selected</span>
                    )
                  }
                />
                <ReviewRow
                  label="Assignment"
                  value={
                    form.assetScope === 'coverage' ? (
                      <div className="text-neutral-600">Coverage owner</div>
                    ) : (
                      <div>
                        <div>{getAssignmentStrategyLabel(form.assignmentStrategy)}</div>
                        {form.assignmentStrategy === 'specific_user' && selectedAssignee && (
                          <div className="text-xs text-neutral-500">{selectedAssignee.fullName}</div>
                        )}
                      </div>
                    )
                  }
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 flex justify-between items-center shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={isFirstStep ? onClose : goBack}
            disabled={submitting}
          >
            {isFirstStep ? 'Cancel' : 'Back'}
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={isReviewStep ? handleSubmit : goNext}
            disabled={!canProceed() || submitting}
          >
            {submitting
              ? 'Saving…'
              : isReviewStep
                ? editing
                  ? 'Save Changes'
                  : 'Create Trigger'
                : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Review row helper
// =============================================================================

const ReviewRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-start gap-4 px-4 py-3">
    <div className="w-32 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-neutral-500 pt-0.5">
      {label}
    </div>
    <div className="flex-1 text-sm text-neutral-800">{value}</div>
  </div>
);

export default TriggerFormModal;
